import { useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Section } from "@/components/Section";
import { CenteredError, Spinner } from "@/components/Spinner";
import { Icon } from "@/components/Icon";
import { InflowLogo } from "@/components/InflowLogo";
import { apiFetch } from "@/api/client";
import { useMarkCourseLessonViewed, useCourse } from "@/api/hooks";

// Юзер 2026-05-23: «під уроками в курсі voting НЕ повинно бути» —
// ⭐-голосування є тільки в KB-уроках, не в курсі.
interface CourseLessonDetail {
  idx: number;
  title: string;
  is_finale: boolean;
  description?: string;
  video_file_id: string;
  video_url: string | null;
}

/**
 * Окремий урок курсу. Відкривається тільки якщо unlocked у списку.
 *
 * До скачування відео через MTProto worker'ом → показуємо плейсхолдер
 * "Відео готується". Після cutover'у + run download_all_course_videos
 * → буде video_url, рендериться <video>.
 */
export function CourseLessonPage() {
  const { idx } = useParams<{ idx: string }>();
  const lessonIdx = Number(idx);

  const q = useQuery({
    queryKey: ["course", "lesson", lessonIdx],
    queryFn: () =>
      apiFetch<CourseLessonDetail>(`/course/lessons/${lessonIdx}`),
    enabled: !Number.isNaN(lessonIdx),
    staleTime: 30_000,
  });

  // Позначаємо урок переглянутим при першому play відео (юзер 2026-06-14).
  // markedRef — щоб не слати POST на кожен play/seek, лише раз за відкриття.
  const markViewed = useMarkCourseLessonViewed();
  const courseQ = useCourse();
  const markedRef = useRef(false);
  const handlePlay = () => {
    if (markedRef.current || Number.isNaN(lessonIdx)) return;
    markedRef.current = true;
    markViewed.mutate(lessonIdx);
  };

  if (q.isLoading) return <Spinner />;
  if (q.isError) return <CenteredError message={q.error.message} />;

  const lesson = q.data!;

  return (
    <>
      {/* Top nav — лого зліва (єдиний хедер) + контроли справа */}
      <div className="mb-4 flex items-center justify-between gap-2">
        <InflowLogo size={18} to="/" />
        <div className="flex items-center gap-2">
        <Link
          to="/course"
          className="flex h-8 items-center gap-1.5 rounded-full bg-surface-soft px-3 font-display text-[0.75rem] font-semibold text-ink shadow-card-soft active:scale-95 transition"
        >
          <Icon
            name="arrow-right"
            size={14}
            className="rotate-180"
            color="#FE6B04"
          />
          Назад
        </Link>
        <Link
          to="/"
          className="flex h-8 items-center gap-1.5 rounded-full bg-surface-soft px-3 font-display text-[0.75rem] font-semibold text-ink shadow-card-soft active:scale-95 transition"
        >
          Меню
          <Icon name="home" size={14} color="#FE6B04" />
        </Link>
        </div>
      </div>

      {/* Брівка на тій самій висоті, що скрізь (owner 2026-09-12). */}
      <div className="mb-3 flex items-center gap-2 pt-4 font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-ink-dim">
        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
        {lesson.is_finale ? "Ефір" : `Урок ${lesson.idx} з 6`}
      </div>

      <h1
        className="mb-4 font-display font-bold text-ink"
        style={{ fontSize: "1.625rem", lineHeight: 1.1, letterSpacing: "-0.02em" }}
      >
        {lesson.title}
      </h1>

      {/* Video card */}
      <div className="mb-6 overflow-hidden rounded-ios-card bg-black shadow-card-soft">
        {lesson.video_url ? (
          <video
            key={lesson.idx}
            src={lesson.video_url}
            controls
            preload="metadata"
            onPlay={handlePlay}
            className="block w-full"
          />
        ) : (
          <div className="bg-surface-soft p-6 text-center">
            <div className="font-display text-[0.8125rem] font-semibold text-ink">
              Відео готується
            </div>
            <div className="mt-1 font-mono text-[0.65625rem] uppercase tracking-[0.08em] text-ink-dim">
              скоро з'явиться
            </div>
          </div>
        )}
      </div>

      {/* Про урок — структурний опис (з транскрипту, юзер 2026-06-11) */}
      {lesson.description && (
        <Section title="Про урок">
          <div className="p-4 text-[0.875rem] leading-relaxed text-ink">
            {lesson.description}
          </div>
        </Section>
      )}

      {/* Навігація уроками: Попередній / Наступний (юзер 2026-06-25).
          На першому — лише «Наступний», на останньому — лише «Попередній».
          «Наступний» з'являється тільки якщо урок розблокований. */}
      {(() => {
        const items = courseQ.data?.items ?? [];
        const pos = items.findIndex((it) => it.idx === lessonIdx);
        const prev = pos > 0 ? items[pos - 1] : null;
        const nextRaw =
          pos >= 0 && pos < items.length - 1 ? items[pos + 1] : null;
        const next = nextRaw && nextRaw.unlocked ? nextRaw : null;
        if (!prev && !next) return null;
        const pill =
          "flex h-11 items-center gap-1.5 rounded-full bg-surface-soft px-4 font-display text-[0.8125rem] font-semibold text-ink shadow-card-soft active:scale-[0.97] transition";
        return (
          <div className="mt-7 flex items-center justify-between gap-2">
            {prev ? (
              <Link to={`/course/${prev.idx}`} className={pill}>
                <Icon
                  name="arrow-right"
                  size={14}
                  className="rotate-180"
                  color="#FE6B04"
                />
                Попередній
              </Link>
            ) : (
              <span />
            )}
            {next ? (
              <Link to={`/course/${next.idx}`} className={pill}>
                Наступний
                <Icon name="arrow-right" size={14} color="#FE6B04" />
              </Link>
            ) : (
              <span />
            )}
          </div>
        );
      })()}
    </>
  );
}
